import React from 'react';
import { useSelector } from 'react-redux';

import { LoadingLogo } from '@fmbt/ui/LoadingSpinner';
import ErrorModal from 'components/modals/ErrorModal';
import AppScaffoldActionType from 'store/modules/appScaffold/constants';
import { selectAppScaffoldInitialDataLoaded } from 'store/modules/appScaffold/selectors';
import { makeSelectFirstErrorMessage } from 'store/modules/error/selectors';
import MyUserActionType from 'store/modules/myUser/constants';
import { selectMyUserExists } from 'store/modules/myUser/selectors';
import { selectIsNetworkReady } from 'store/modules/network/selectors';

const actions = [
  MyUserActionType.LOAD_MY_USER_REQUEST,
  AppScaffoldActionType.LOAD_APP_SCAFFOLD_REQUEST
];

export const selectErrorMessage = makeSelectFirstErrorMessage(actions);

interface Props {
  children: JSX.Element;
}

/**
 * Prevents children from rendering while my user data is loading.
 * Will display an error modal in the event of an error.
 *
 * Note: Authentication is applied by the caller (Routes) so that
 * public routes can bypass the Auth0 redirect.
 */
function ProtectedRoutesLoadingGate({ children }: Props): JSX.Element {
  const myUserExists = useSelector(selectMyUserExists);
  const appScaffoldInitialDataLoaded = useSelector(
    selectAppScaffoldInitialDataLoaded
  );
  const isNetworkReady = useSelector(selectIsNetworkReady);
  const errorMessage = useSelector(selectErrorMessage);

  // Waiting for
  // 1) myUser to be fetched from BE
  // 2) app scaffold data to be loaded from BE or redux
  if (myUserExists && appScaffoldInitialDataLoaded && isNetworkReady) {
    return children;
  }

  if (errorMessage) {
    return <ErrorModal errorMessage={errorMessage} />;
  }

  return <LoadingLogo />;
}

export default ProtectedRoutesLoadingGate;
